import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { DebugStats } from '../ui/DebugHUD';
import type { GameStateInfo } from '../ui/GameStateHUD';
import type { RenderPipeline } from '../renderer/RenderPipeline';
import type { WorldManager } from '../world/WorldManager';
import type { WorldInfo } from '../config/WorldConfig';
import type { TravelStressTest } from '../systems/TravelStressTest';

/**
 * Bridges stats from game systems to UI overlays.
 * Runs per frame — reads from Pipeline/World/Fog, writes to HUD data objects.
 */
export class StatsBridge implements GameModule {
  readonly name = 'StatsBridge';

  private _stats: DebugStats;
  private _state: GameStateInfo;
  private _pipeline: RenderPipeline;
  private _world: WorldManager;
  private _worldInfo: WorldInfo;
  private _stressTest: TravelStressTest | null = null;

  constructor(
    stats: DebugStats, state: GameStateInfo,
    pipeline: RenderPipeline, world: WorldManager, worldInfo: WorldInfo,
  ) {
    this._stats = stats;
    this._state = state;
    this._pipeline = pipeline;
    this._world = world;
    this._worldInfo = worldInfo;
  }

  setStressTest(t: TravelStressTest | null): void { this._stressTest = t; }

  init(_engine: Engine): void {}

  update(): void {
    this._stats.drawCalls = this._pipeline.drawCallCount;
    this._stats.activeClusters = this._world.clusterManager.activeCount;
    this._stats.maxClusters = this._worldInfo.config.maxActiveClusters;
    this._stats.hexCount = this._world.hexRenderer.hexCount;
    this._stats.totalHexes = this._worldInfo.totalHexes;

    if (this._world.contentManager) {
      this._state.starCount = this._world.contentManager.loadedStars;
      this._state.planetCount = this._world.contentManager.loadedPlanets;
    }
    if (this._world.fogManager && this._world.fogState) {
      this._state.fogReveals = this._world.fogState.revealCount;
      this._state.fogHexes = this._world.fogManager.fogHexCount;
    }
    this._stats.stressRoutes = this._stressTest?.state.routeCount ?? 0;
    this._stats.stressAvailable = this._stressTest !== null;
    this._stats.fogAvailable = this._world.fogManager !== null;
  }

  dispose(): void {}
}
